/**
 * MedicalClearancePicker - Wrapper around generic EntityPicker
 * 
 * Provides medicalClearance-specific configuration and maintains backward compatibility
 * with existing code that imports MedicalClearancePicker or MedicalClearancePickerModal
 */

import React, { useMemo } from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';

// ── MedicalClearance-specific configuration ────────────────────────────────────────────
export const MEDICAL_CLEARANCE_CONFIG = {
	apiEndpoint: "/api/MedicalClearance/unused-completed-list",
	entityName: "MedicalClearance",
	idField: "id",
	displayFields: {
		primary: "id",
		secondary: "referenceNumber"
	},
	tableHeaders: ["Id", "Reference #"],
	tableAccessors: ["id", "referenceNumber"],
	searchFields: [
		{ label: "All", field: "all", index: 0 },
		{ label: "Medical Clearance Id", field: "id", index: 1 },
		{ label: "Medical Clearance Reference #", field: "referenceNumber", index: 2 },
	],
	placeholder: "--Select medical clearance--"
};

// ── MedicalClearancePicker wrapper ────────────────────────────────────────────────
/**
 * MedicalClearancePicker
 * 
 * Usage:
 * <MedicalClearancePicker
 *   label="MedicalClearance Code"
 *   medicalClearanceId={form.medicalClearanceId}
 *   displayValue={form.medicalClearanceReferenceNumber}
 *   onSelect={(medicalClearance) => {
 *     set("medicalClearanceId", medicalClearance.id);
 *     set("medicalClearanceReferenceNumber", medicalClearance.code);
 *   }}
 *   onClear={() => {
 *     set("medicalClearanceId", "");
 *     set("medicalClearanceReferenceNumber", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function MedicalClearancePicker({
	medicalClearanceId,           // medical clearance-specific prop name
	filterReferenceId = 0, // for filtering the medical clearances list
	displayValue = "",
	onSelect,
	onClear,
	...rest             // label, isRequired, error, disabled, etc.
}) {

	// easier way to update url link based on the api param needs
	// eg: /api/MedicalClearance/unused-completed-list/123
	const config = useMemo(() => {
		let endpoint = MEDICAL_CLEARANCE_CONFIG.apiEndpoint;

		if (filterReferenceId) {
			endpoint = `${endpoint}/${filterReferenceId}`;
		}

		return {
			...MEDICAL_CLEARANCE_CONFIG,
			apiEndpoint: endpoint,
		};
	}, [filterReferenceId]);

	return (
		<EntityPickerField
			{...rest}
			config={config}
			entityId={medicalClearanceId}
			displayValue={displayValue}
			onSelect={onSelect}
			onClear={onClear}
		/>
	);
}

// ── MedicalClearancePickerModal wrapper ────────────────────────────────────────────────
/**
 * MedicalClearancePickerModal
 * 
 * Usage:
 * <MedicalClearancePickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(medicalClearance) => {
 *     set("medicalClearanceId", medicalClearance.id);
 *     set("medicalClearanceReferenceNumber", medicalClearance.code);
 *   }}
 * />
 */
export function MedicalClearancePickerModal({ isOpen, onClose, setData }) {
	return (
		<EntityPickerModal
			isOpen={isOpen}
			onClose={onClose}
			setData={setData}
			config={MEDICAL_CLEARANCE_CONFIG}
		/>
	);
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { MedicalClearancePicker as default };
